import React, { useMemo, useState } from "react";
import {
  makeImageFromUri,
  resizeImageAsBlob,
} from "components/utility/image-upload";
import { useUser } from "contexts/UserContext";
import putOrchidProCustomLogo from "services/putOrchidProCustomLogo";
import { ReactComponent as OutlinePlus } from "assets/svg/outline-plus.svg";
import { apiHostAndPath } from "utils/api";
import styles from "./ImageUpload.module.scss";

export const ImageUpload = () => {
  const { user } = useUser();
  const [imgUpdateTimestamp, setImgUpdateTimestamp] = useState(Date.now());
  const [imageError, setImageError] = useState(false);

  const [dragging, setDragging] = useState(false);

  let imagePreviewUrl = user
    ? apiHostAndPath(
        `/api/users/v1/orchid_pro/custom_logo?timestamp=${imgUpdateTimestamp}`,
      )
    : null;

  const imagePreview = useMemo(() => {
    if (!imagePreviewUrl) return undefined;
    return (
      <img
        src={imagePreviewUrl}
        onError={() => setImageError(true)}
        onClick={() => {
          document.getElementById("file-upload")?.click();
        }}
        alt="orchid-profile-img"
        className={styles["profile-img"]}
        style={{ cursor: "pointer" }}
      />
    );
  }, [imagePreviewUrl]);

  const onImageChanged = async (imagePreviewUrl) => {
    try {
      const img = await makeImageFromUri(imagePreviewUrl);
      const blob = await resizeImageAsBlob(img, 800, 800);
      await putOrchidProCustomLogo(blob);
      setImgUpdateTimestamp(Date.now());
    } catch (err) {
      console.error("Failed to set practice logo", err);
    }
  };

  const handleImageChange = (e) => {
    e.preventDefault();
    let reader = new FileReader();
    let file = e.target.files[0];
    reader.onloadend = (e) => {
      onImageChanged(e.target?.result);
    };
    if (file) reader.readAsDataURL(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    let reader = new FileReader();
    let file = e.dataTransfer.files[0];
    reader.onloadend = (e) => {
      onImageChanged(e.target?.result);
    };
    if (file) reader.readAsDataURL(file);
  };

  return (
    <div
      className={`${styles["image-upload-container"]} ${
        dragging ? styles["dragging"] : ""
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Hidden input always present so clicks on preview can trigger it */}
      <input
        id="file-upload"
        type="file"
        style={{ display: "none" }}
        onChange={handleImageChange}
      />
      {imagePreview && !imageError ? (
        <div className={styles["image-preview"]}>{imagePreview}</div>
      ) : (
        <div style={{ padding: "42px 36px" }}>
          <p>Drag & Drop your file(s) here</p>
          <p>OR</p>
          <label htmlFor="file-upload" className={styles["upload-button"]}>
            <OutlinePlus className={styles["icon"]} />
            <span>Upload from computer</span>
          </label>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
